import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, TextField, Typography, Link } from '@material-ui/core/'
import { makeStyles } from '@material-ui/core/styles'

import api from 'common/api'
import useScid from 'common/use-scid'

import AppStatusRefresher from '../AppStatusRefresher'
import { APPLICATION_STATUS } from '../../constants'

export const REFRESH_INTERVAL = 5000

const useStyles = makeStyles((theme) => ({
  card: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
  },
  testIntegrationButton: {
    width: theme.typography.pxToRem(240),
  },
  verificationInputContainer: {
    marginTop: theme.spacing(3),
    maxWidth: theme.spacing(64),
  },
  verificationInput: {
    marginRight: theme.spacing(2),
    maxWidth: theme.spacing(52),
  },
}))

const VerifyApplicationInitiation = ({ appConfig, refetchAppConfig, markStepComplete, readonly }) => {
  const styles = useStyles()
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationCodeError, setVerificationCodeError] = useState()
  const scid = useScid()
  const verifyAppInitiation = () => {
    api
      .put(
        `/api/colleges/${scid}/integrations/application/configuration/${appConfig.id}/verification/applicationInitiation`,
        verificationCode,
      )
      .then(async () => {
        setVerificationCodeError()
        markStepComplete()
      })
      .catch(() => {
        setVerificationCodeError(
          <p>
            Incorrect <strong>coalitionId</strong>. Ensure you&apos;re looking at the most recent mock application in{' '}
            <strong>CoalitionApplicationProfile</strong>.
          </p>,
        )
      })
  }
  const verificationInProgress = appConfig && appConfig.verificationAppId
  const appStatus = appConfig && appConfig.verificationAppStatus
  const initiationVerified = appConfig && appConfig.verification && appConfig.verification.initiationVerified

  return (
    <React.Fragment>
      <hr />
      <Box mt={3}>
        <Typography>
          If the mock application was sent successfully, the status below should update to <strong>Initiated</strong>.
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
        >
          If the status does not change after refreshing please{' '}
          <Link
            href="https://www.scoir.com/support"
            target="_blank"
          >
            Contact Scoir Support.
          </Link>
        </Typography>
        <AppStatusRefresher
          status={appStatus}
          refresh={refetchAppConfig}
          shouldRefresh={verificationInProgress && appStatus !== APPLICATION_STATUS.Initiated && !readonly}
          refreshInterval={REFRESH_INTERVAL}
        />
        <Typography>
          Now view the application in Slate, copy the <strong>coalitionId</strong>, and enter it below to verify the
          connection between Scoir and Slate.
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
        >
          You can find this ID by opening up your most recent record in the <strong>CoalitionApplicationProfile</strong> source format and viewing its details.
        </Typography>
        <Box
          display="flex"
          className={styles.verificationInputContainer}
        >
          <TextField
            className={styles.verificationInput}
            variant="outlined"
            size="small"
            type="text"
            label="Coalition Id"
            onChange={(e) => setVerificationCode(e.target.value)}
            value={verificationCode}
            error={!!verificationCodeError}
            disabled={initiationVerified || readonly || appStatus !== APPLICATION_STATUS.Initiated}
            fullWidth
            required
          />
          <Button
            color="primary"
            variant="contained"
            onClick={verifyAppInitiation}
            disabled={!verificationCode || initiationVerified || readonly}
          >
            Submit
          </Button>
        </Box>
        {verificationCodeError && (
          <Box mt={2}>
            <Typography
              data-testid="verification-code-error"
              variant="caption"
              color="error"
            >
              {verificationCodeError}
            </Typography>
          </Box>
        )}
      </Box>
    </React.Fragment>
  )
}

VerifyApplicationInitiation.propTypes = {
  appConfig: PropTypes.object.isRequired,
  refetchAppConfig: PropTypes.func.isRequired,
  markStepComplete: PropTypes.func,
  readonly: PropTypes.bool,
}

export default VerifyApplicationInitiation
